import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Box, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import type { Reminder } from "@/interfaces/reminders";
import ReminderDateTimePicker from "./ReminderDateTimePicker";

export const FADE_MS = 400;

export interface ReminderEmailUpcomingProps {
  reminder: Reminder;
  onDelete: () => void;
  onChange: (date: string, time: string) => void;
  isFading: boolean;
  disabled?: boolean;
  /** Calendar bounds for THIS row, from `reminderDateBounds` — the campaign's send/expiry window
   *  narrowed by the neighbouring reminders. Forwarded untouched to the picker. */
  minDate?: string;
  maxDate?: string;
}

export default function ReminderEmailUpcoming({
  reminder,
  onDelete,
  onChange,
  isFading,
  disabled,
  minDate,
  maxDate,
}: ReminderEmailUpcomingProps) {
  const [delHover, setDelHover] = useState(false);
  const isScheduled = Boolean(reminder.date);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        transition: `opacity ${FADE_MS}ms ease, transform ${FADE_MS}ms ease`,
        opacity: isFading ? 0 : 1,
        transform: isFading ? "translateX(48px)" : "translateX(0)",
      }}
    >
      {/* Trigger row: date field + scheduled label + delete button */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <ReminderDateTimePicker
          value={{ date: reminder.date, time: reminder.time }}
          onChange={onChange}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
        />

        {/* Scheduled label + delete — aligned to field height */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: 52, flexShrink: 0 }}>
          {isScheduled && (
            <Typography
              sx={{ fontSize: 14, color: "var(--color-dark-dust)", whiteSpace: "nowrap" }}
            >
              Scheduled
            </Typography>
          )}
          <IconButton
            size="small"
            onClick={onDelete}
            disabled={disabled}
            onMouseEnter={() => setDelHover(true)}
            onMouseLeave={() => setDelHover(false)}
            sx={{
              border: "1px solid",
              borderColor: delHover ? "var(--color-good-wine)" : "var(--color-smoke)",
              borderRadius: "var(--radius-btn, 6px)",
              width: 52,
              height: 52,
              color: delHover ? "var(--color-good-wine)" : "var(--color-graphite)",
              bgcolor: delHover ? "var(--color-good-wine-bg)" : "background.paper",
              transition: "all 0.15s ease",
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}
